/**
 * EditAgentConnectionDialog — first-class edit surface for an agent connection
 * stored under `agentConnections` in `connections.json` (or the draft file in
 * draft mode).
 *
 * Reuses `AgentConnectionForm` seeded from the persisted `AgentConnection`.
 * The agent form is a controlled component (values + onChange owned by the
 * parent), so this dialog manages the form state, dirty tracking, and
 * discard-confirm chrome. Persistence delegates to the caller-supplied
 * `onSave`, typically `useConnectionsEditor.handleSaveLocalConnection`, so
 * draft/prod routing stays authoritative.
 *
 * Scope notes:
 * - No OAuth re-authorization for GitHubCopilot in this pass — the connection
 *   already has a valid token from creation. Users can rotate via delete +
 *   recreate if needed. Renaming is still supported.
 * - API-key changes use the same app-setting extraction path as creation, so
 *   secrets are never written inline to connections.json.
 * - Subscription/account/project ARM IDs can be re-selected via the cascading
 *   dropdowns, but changing them effectively re-targets the connection and is
 *   an advanced operation. The form's normal validation applies.
 */

import { useCallback, useMemo, useState } from 'react';
import {
  Button,
  Dialog,
  DialogActions,
  DialogBody,
  DialogContent,
  DialogSurface,
  DialogTitle,
  makeStyles,
  tokens,
} from '@coreai/fluentui-react';
import { AgentConnectionForm } from '@/components/WorkflowDesigner/NodeEditor/connections/forms/AgentConnectionForm';
import type { AgentConnectionFormValues, AgentModelType } from '@/types/agentConnection';
import {
  extractAgentConnectionAppSettings,
  getAgentAuthenticationMode,
  isAgentConnectionFormValid,
  isFoundryModelType,
  normalizeArmResourceId,
  normalizeProviderName,
} from '@/utils/connections';
import type { AgentConnection } from '@/types/logicApps';
import {
  useEnvironmentSettings,
  useRollbackEnvironmentSettingChanges,
  useTransactionalUpsertEnvironmentSettings,
} from '@/hooks/useEnvironmentSettings';
import { useAppStore } from '@/stores/appStore';
import { useAuth } from '@/auth/useAuth';
import { ensureAzureOpenAIPermissions } from '@/services/roleAssignmentApi';
import { isLocalMode } from '@/services/localRuntimeClient';
import { DiscardChangesDialog } from './editForm/DiscardChangesDialog';
import { useDiscardGuard } from './editForm/useDiscardGuard';

const useStyles = makeStyles({
  dialogSurface: {
    maxWidth: '720px',
    minWidth: '560px',
  },
  body: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalM,
    minHeight: '360px',
  },
  errorBanner: {
    padding: tokens.spacingVerticalS,
    paddingLeft: tokens.spacingHorizontalS,
    borderRadius: tokens.borderRadiusMedium,
    backgroundColor: tokens.colorPaletteRedBackground1,
    color: tokens.colorPaletteRedForeground1,
    fontSize: tokens.fontSizeBase200,
  },
});

export interface EditAgentConnectionDialogProps {
  open: boolean;
  onClose: () => void;
  connectionKey: string;
  connection: AgentConnection;
  isDraftMode?: boolean;
  applicationContext?: {
    projectName: string;
    appName: string;
    principalId?: string;
  };
  onSave: (recordKey: string, record: AgentConnection) => Promise<void>;
}

/** Extract the `{subId}` segment from an ARM resource ID, if present. */
function parseSubscriptionId(armId?: string): string | undefined {
  if (!armId) return undefined;
  const m = armId.match(/\/subscriptions\/([^/]+)/i);
  return m?.[1];
}

/**
 * Infer the `AgentModelType` from a persisted `AgentConnection`. Prefers the
 * explicit `type` field (which `handleCreateAgentConnection` writes as the
 * concrete provider name), then falls back to `modelProvider.type`, then to
 * endpoint-based heuristics. When nothing matches we default to
 * `V1ChatCompletionsService` — the most permissive edit surface (manual
 * endpoint + key) so the dialog is at least openable.
 */
function inferAgentModelType(conn: AgentConnection): AgentModelType {
  const t = conn.type ?? conn.modelProvider?.type;
  if (
    t === 'AzureOpenAI' ||
    t === 'FoundryAgentService' ||
    t === 'FoundryAgentServiceV2' ||
    t === 'APIMGenAIGateway' ||
    t === 'GitHubCopilot' ||
    t === 'V1ChatCompletionsService'
  ) {
    return t;
  }
  if (conn.resourceId && /\/projects\//i.test(conn.resourceId)) {
    return 'FoundryAgentService';
  }
  const normalizedEndpointProvider = normalizeProviderName(conn.endpoint);
  if (normalizedEndpointProvider === 'AzureOpenAI') {
    return 'AzureOpenAI';
  }
  if (normalizedEndpointProvider === 'OpenAI') {
    return 'V1ChatCompletionsService';
  }
  const endpoint = conn.endpoint?.toLowerCase() ?? '';
  if (endpoint.includes('githubcopilot')) return 'GitHubCopilot';
  if (endpoint.includes('services.ai.azure.com')) return 'FoundryAgentService';
  return 'V1ChatCompletionsService';
}

/**
 * Seed `AgentConnectionFormValues` from the persisted connection. Maps
 * `resourceId` to `accountId` (AzureOpenAI) or `projectId` (Foundry) so the
 * cascading dropdowns render pre-selected.
 */
function seedFormValues(
  conn: AgentConnection,
  modelType: AgentModelType,
): AgentConnectionFormValues {
  // With `exactOptionalPropertyTypes: true`, optional fields must be omitted
  // (not set to `undefined`), so build the object incrementally.
  const base: AgentConnectionFormValues = { displayName: conn.displayName ?? '' };
  if (conn.endpoint !== undefined) base.endpoint = conn.endpoint;
  if (conn.authentication?.key !== undefined) base.apiKey = conn.authentication.key;
  if (modelType === 'AzureOpenAI') {
    base.authenticationMode =
      conn.authentication?.type === 'ManagedServiceIdentity'
        ? 'managedIdentity'
        : 'apiKey';
  } else if (isFoundryModelType(modelType)) {
    base.authenticationMode = 'managedIdentity';
  } else if (
    modelType === 'APIMGenAIGateway' ||
    modelType === 'V1ChatCompletionsService'
  ) {
    base.authenticationMode = 'apiKey';
  }

  const subscriptionId = parseSubscriptionId(conn.resourceId);
  if (subscriptionId !== undefined) base.subscriptionId = subscriptionId;

  if (modelType === 'AzureOpenAI') {
    if (conn.resourceId !== undefined) base.accountId = conn.resourceId;
  } else if (isFoundryModelType(modelType)) {
    if (conn.resourceId !== undefined) base.projectId = conn.resourceId;
  }
  return base;
}

/**
 * Fold updated form values back into the original `AgentConnection`. Fields
 * that don't have a first-class control (`modelProvider`, `modelConfiguration`,
 * `parameterValues`) are preserved verbatim from the original record.
 */
function buildAgentConnection(
  values: AgentConnectionFormValues,
  modelType: AgentModelType,
  original: AgentConnection,
): AgentConnection {
  const updated: AgentConnection = {
    ...original,
    displayName: values.displayName,
  };
  if (values.endpoint !== undefined) {
    updated.endpoint = values.endpoint;
  }
  if (modelType === 'AzureOpenAI' && values.accountId) {
    updated.resourceId = values.accountId;
    updated.type = 'Model';
  } else if (isFoundryModelType(modelType) && values.projectId) {
    updated.resourceId = values.projectId;
  }

  const authType = original.authentication?.type;
  const authenticationMode = getAgentAuthenticationMode(modelType, values);
  if (authType === 'None') {
    if (original.authentication !== undefined) {
      updated.authentication = original.authentication;
    }
  } else if (authenticationMode === 'managedIdentity') {
    const canPreserveManagedIdentityMetadata =
      authType === 'ManagedServiceIdentity' &&
      (
        modelType !== 'AzureOpenAI' ||
        normalizeArmResourceId(original.resourceId) ===
          normalizeArmResourceId(values.accountId)
      );
    updated.authentication =
      canPreserveManagedIdentityMetadata && original.authentication
        ? original.authentication
        : { type: 'ManagedServiceIdentity' };
  } else if (values.apiKey !== undefined) {
    updated.authentication = {
      type: 'Key',
      key: values.apiKey,
    };
  }
  return updated;
}

export const EditAgentConnectionDialog = ({
  open,
  onClose,
  connectionKey,
  connection,
  isDraftMode = false,
  applicationContext,
  onSave,
}: EditAgentConnectionDialogProps) => {
  const styles = useStyles();
  const { getArmToken, getOttoToken } = useAuth();
  const fallbackProject = useAppStore((state) => state.selectedProject);
  const fallbackAppName = useAppStore((state) => state.selectedAppName);
  const fallbackAppPrincipalId = useAppStore(
    (state) => state.selectedApp?.identity?.principalId,
  );
  const projectName = applicationContext?.projectName ?? fallbackProject ?? '';
  const appName = applicationContext?.appName ?? fallbackAppName ?? '';
  const appPrincipalId =
    applicationContext === undefined
      ? fallbackAppPrincipalId
      : applicationContext.principalId;
  const { data: prodSettings } = useEnvironmentSettings(
    projectName,
    appName,
    false,
  );
  const upsertAppSettings = useTransactionalUpsertEnvironmentSettings(
    projectName,
    appName,
    isDraftMode,
    prodSettings ?? [],
  );
  const rollbackAppSettings = useRollbackEnvironmentSettingChanges(
    projectName,
    appName,
    isDraftMode,
    prodSettings ?? [],
  );

  const modelType = useMemo(() => inferAgentModelType(connection), [connection]);
  const initialValues = useMemo(
    () => seedFormValues(connection, modelType),
    [connection, modelType],
  );
  const [values, setValues] = useState<AgentConnectionFormValues>(initialValues);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [saveError, setSaveError] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);

  const isDirty = useMemo(
    () => JSON.stringify(values) !== JSON.stringify(initialValues),
    [values, initialValues],
  );

  const handleFormChange = useCallback((next: AgentConnectionFormValues) => {
    setValues(next);
    // Clear stale field errors as the user types — matches `EditConnectionDialog`.
    setErrors({});
    setSaveError(null);
  }, []);

  const handleSave = useCallback(async () => {
    setSaveError(null);

    // Reuse the create-flow's validity check. It already branches per
    // model type and requires displayName + the fields relevant to the
    // provider (subscription/account for AzureOpenAI, endpoint/key for
    // manual entry, etc.). GHCP is a special case here: the create flow
    // requires OAuth completion, but edit mode doesn't re-auth, so we only
    // need displayName to be non-empty.
    const displayNameOk = !!values.displayName.trim();
    const providerOk =
      modelType === 'GitHubCopilot'
        ? true
        : isAgentConnectionFormValid(modelType, values);
    if (!displayNameOk || !providerOk) {
      const fieldErrors: Record<string, string> = {};
      if (!displayNameOk) fieldErrors.displayName = 'Connection name is required';
      if (!providerOk) {
        if (modelType === 'AzureOpenAI') {
          if (!values.subscriptionId) fieldErrors.subscriptionId = 'Subscription is required';
          if (!values.accountId) fieldErrors.accountId = 'Azure OpenAI resource is required';
        } else if (isFoundryModelType(modelType)) {
          if (!values.subscriptionId) fieldErrors.subscriptionId = 'Subscription is required';
          if (!values.projectId) fieldErrors.projectId = 'AI Foundry project is required';
          if (!values.endpoint) fieldErrors.endpoint = 'Project endpoint is required';
        } else {
          if (!values.endpoint) fieldErrors.endpoint = 'Endpoint URL is required';
          if (!values.apiKey) {
            fieldErrors.apiKey = 'API key is required';
          }
        }
      }
      setErrors(fieldErrors);
      return;
    }

    setIsSaving(true);
    try {
      const updated = buildAgentConnection(values, modelType, connection);
      const initialAuthenticationMode = getAgentAuthenticationMode(
        modelType,
        initialValues,
      );
      const authenticationMode = getAgentAuthenticationMode(modelType, values);
      const requiresAzureOpenAIRbac =
        modelType === 'AzureOpenAI' &&
        authenticationMode === 'managedIdentity' &&
        (
          initialAuthenticationMode !== 'managedIdentity' ||
          normalizeArmResourceId(initialValues.accountId) !==
            normalizeArmResourceId(values.accountId)
        );

      if (requiresAzureOpenAIRbac && values.accountId && !isLocalMode()) {
        const armToken = await getArmToken();
        if (!armToken) {
          throw new Error('Could not acquire an Azure token to verify managed identity access.');
        }
        const ottoToken = appPrincipalId ? undefined : await getOttoToken();
        if (!appPrincipalId && !ottoToken) {
          throw new Error('Could not retrieve the workflow app managed identity.');
        }
        const roleResult = await ensureAzureOpenAIPermissions(
          values.accountId,
          armToken,
          {
            projectName,
            appName,
            ...(ottoToken ? { ottoToken } : {}),
            ...(appPrincipalId
              ? { principalId: appPrincipalId }
              : {}),
          },
        );
        if (!roleResult.success) {
          throw new Error(
            roleResult.error ??
              'Could not grant the workflow app managed identity access to the Azure OpenAI account.',
          );
        }
      }

      let wroteKeyAppSettings = false;
      let writtenAppSettings: Record<string, string> = {};
      let previousSettingsSnapshot:
        | Array<{ name: string; value: string | null }>
        | undefined;
      if (updated.authentication?.type === 'Key') {
        const { authentication, appSettings } =
          extractAgentConnectionAppSettings(
            connectionKey,
            updated.authentication,
          );
        const appSettingNames = Object.keys(appSettings);
        writtenAppSettings = appSettings;
        if (appSettingNames.length > 0) {
          if (!projectName || !appName) {
            throw new Error(
              'No environment/app context available — cannot persist secret app settings.',
            );
          }
          const result = await upsertAppSettings.mutateAsync(appSettings);
          previousSettingsSnapshot = result.previousSettings;
          wroteKeyAppSettings = true;
        }
        if (authentication?.type) {
          updated.authentication = {
            ...authentication,
            type: authentication.type,
          };
        }
      }

      try {
        await onSave(connectionKey, updated);
      } catch (saveError) {
        if (wroteKeyAppSettings) {
          try {
            await rollbackAppSettings.mutateAsync({
              writtenSettings: writtenAppSettings,
              previousSettings: previousSettingsSnapshot ?? [],
            });
          } catch (rollbackError) {
            const rollbackMessage =
              rollbackError instanceof Error
                ? rollbackError.message
                : 'unknown rollback error';
            throw new Error(
              `Connection save failed and the previous environment settings could not be restored: ${rollbackMessage}`,
            );
          }
        }
        throw saveError;
      }
      onClose();
    } catch (err) {
      setSaveError(err instanceof Error ? err.message : 'Failed to save connection');
    } finally {
      setIsSaving(false);
    }
  }, [
    values,
    modelType,
    connection,
    initialValues,
    getArmToken,
    getOttoToken,
    appPrincipalId,
    projectName,
    appName,
    upsertAppSettings,
    rollbackAppSettings,
    onSave,
    connectionKey,
    onClose,
  ]);

  // Agent dialog has no form snapshot to restore, so no `onDiscard` cleanup —
  // confirming discard simply closes.
  const discardGuard = useDiscardGuard({ isDirty, isBusy: isSaving, onClose });

  return (
    <>
    <Dialog
      open={open}
      onOpenChange={discardGuard.handleOpenChange}
      modalType="modal"
    >
      <DialogSurface className={styles.dialogSurface}>
        <DialogBody>
          <DialogTitle>Edit agent connection</DialogTitle>
          <DialogContent className={styles.body}>
            <AgentConnectionForm
              agentModelType={modelType}
              values={values}
              onChange={handleFormChange}
              errors={errors}
              disabled={isSaving}
              // Edit mode: never let the AzureOpenAI section auto-fetch the
              // account's endpoint/key from ARM on mount. The persisted values
              // (which may be `@appsetting(...)` refs) are the source of truth
              // until the user explicitly changes something; auto-filling
              // would rewrite them and flip the form's dirty state before the
              // user has done anything.
              autoFillCredentials={
                modelType === 'AzureOpenAI' &&
                (
                  normalizeArmResourceId(values.accountId) !==
                    normalizeArmResourceId(initialValues.accountId) ||
                  getAgentAuthenticationMode(modelType, values) !==
                    getAgentAuthenticationMode(modelType, initialValues)
                )
              }
              // GHCP already-authenticated: skip the OAuth button and show the
              // "already signed in" preview. Edit mode never re-authorizes.
              isOAuthComplete={modelType === 'GitHubCopilot'}
            />
            {saveError && (
              <div className={styles.errorBanner} role="alert">
                {saveError}
              </div>
            )}
          </DialogContent>
          <DialogActions>
            <Button appearance="secondary" onClick={discardGuard.requestClose} disabled={isSaving}>
              Cancel
            </Button>
            <Button
              appearance="primary"
              onClick={handleSave}
              disabled={isSaving || !isDirty}
            >
              {isSaving ? 'Saving…' : 'Save'}
            </Button>
          </DialogActions>
        </DialogBody>
      </DialogSurface>
    </Dialog>

    {/* Discard-confirmation dialog — shared across every ConnectionList
        edit surface for consistent copy + affordances. */}
    <DiscardChangesDialog
      open={discardGuard.showDiscardConfirm}
      onKeepEditing={discardGuard.keepEditing}
      onDiscard={discardGuard.confirmDiscard}
    />
    </>
  );
};

EditAgentConnectionDialog.displayName = 'EditAgentConnectionDialog';
